import React, { ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import * as _ from "lodash";

import SubmitPostView from './components/Post/Submission/SubmitPostView';
import styled from 'styled-components/macro';
import GlobalNotificationDialog from './components/App/GlobalNotificationDialog';
import { Switch, Route, useLocation, Redirect } from 'react-router';
import MyFeedbackView from './components/Post/MyFeedback/MyFeedbackView';
import { AppPaths } from './constants/urls';
import NoMatch from './components/App/NoMatch';
import { useSelector } from 'react-redux';
import { RootState } from './store/rootState';
import NavBar from './components/App/NavBar';
import Breadcrumb from './components/App/Breadcrumb';
import MyActivityView from './components/Post/MyFeedback/MyActivityView';
import ProfileView from './components/Post/MyFeedback/ProfileView';
import { forMinWidth } from './theme/breakpoints';
import { comments, postId, spaceSlug, title, UrlParam } from './constants/urlParams';
import PostDetailsView from './components/Post/Details/PostDetailsView';
import { HashLocatorContext } from './components/shared/HashLocator';
import DisconnectedFromNativeDialog from './components/App/DisconnectedFromNativeDialog';
import SearchView from './components/Post/Search/SearchView';
import HomeView from './components/Post/Home/HomeView';
import RecentFixesView from './components/Post/Fixes/RecentFixesView';
import OutdatedBuildBanner from './components/shared/OutdatedBuildBanner'
import { ReleaseAction } from './store/appState';
import PostDetailsRewriteView from './components/Post/Details/PostDetailsRewriteView';
import ChooseAccount from './components/App/ChooseAccount';
import SiteHeader from './components/App/SiteHeader';
import * as Actions from './store/actions';
import { SpaceConfig } from './config/spaces/spaceConfig';
import { SpaceId } from './config/spaces/spaceIds';
import { getSpaceConfig, instance } from './config/serverConfig';
import { useQueryParam } from './hooks/routing';
import { getSpaceIdFromSlug } from './config/serverConfig';
import { getPotentialSlugFromPathName } from './utils/webPage';
import { App, SignInState } from './app';

// As a precaution disable the capability to make website readonly until couple of days before we need it
// So that any accidental setting of app setting in azure doesn't make PROD readonly 
const allowReadOnlyModeCapability: boolean = false;

export default function AppReact(): ReactElement {
    const state = useSelector((state: RootState) => state.appState);
    const isDisconnectedFromNativeProcess = useSelector((state: RootState) => state.appState.disconnectedFromNativeProcess);
    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);
    const [isNewContentAvailable, setIsNewContentAvailable] = useState(true);
    const [foundHashAnchor, setFoundHashAnchor] = useState(false);
    const location = useLocation();

    useEffect(() => {
        if (allowReadOnlyModeCapability) {
            Actions.setSiteReadOnlySetting();
        }
    }, []);

    const locateHash = useCallback(
        _.debounce((isNewContentAvailable: boolean, foundHashAnchor: boolean) => {
            if (isNewContentAvailable && !foundHashAnchor) {
                const id = location.hash.replace("#", "");
                const element = document.getElementById(id);
                setIsNewContentAvailable(false);

                if (element) {
                    setTimeout(() => {
                        // 'page-content' is the main content element used as target for 'skip to main content' button. Ignore highlighting it
                        if (id !== "page-content") {
                            element.style.outline = "rgb(75 6 193 / 35%) dashed 5px";

                            window.onclick = () => {
                                element.style.outline = "none";
                            }
                        }

                        element.scrollIntoView();

                        if (id !== "page-content") {
                            // shift slightly so as to show highlighted content more clearly
                            setTimeout(() => {
                                window.scrollBy(0, -100);
                            }, 200);
                        }

                        setFoundHashAnchor(true);
                    }, 100);
                }
            }
        }, 400)
        , [location, isNewContentAvailable, foundHashAnchor]
    );

    // Some new element deep in the UI tree finished loading and might want us to scroll to it if the url specifies so
    useEffect(() => {
        locateHash(isNewContentAvailable, foundHashAnchor);
    }, [locateHash]);

    // recolor site based on space (Azure, VS, ...)
    useEffect(() => {
        const maybeSlug: string = getPotentialSlugFromPathName(location.pathname);
        const spaceId: SpaceId | null = getSpaceIdFromSlug(maybeSlug);
        const spaceConfig: SpaceConfig = getSpaceConfig(spaceId ?? SpaceId.VS);
        if (spaceConfig.accentColor) {
            document.documentElement.style.setProperty('--color-primary', spaceConfig.accentColor);
        }
        else {
            try {
                document.documentElement.style.removeProperty('--color-primary');
            }
            catch {
                // remove failed, ignore; see https://developer.mozilla.org/en-US/docs/Web/API/CSSStyleDeclaration/removeProperty
            }
        }
    }, [location]);

    const signInRequired: boolean = instance.isInternal;

    // show data on if sign in required only on successful sign in OR if anonymous allowed
    const shouldShowData: boolean = (signInRequired && state.signInState === SignInState.Success) ||
        (!signInRequired && state.signInState !== SignInState.ChooseAccount);
    return (
        <Wrapper>
            <DisconnectedFromNativeDialog
                isOpen={isDisconnectedFromNativeProcess}
            />

            <HashLocatorContext.Provider value={{ newContentWasLoaded: () => setIsNewContentAvailable(true) }}>
                <div css="display: flex; flex-direction: column; flex-grow: 1">
                    <SiteHeader />

                    <Content id="page-content" tabIndex={-1}>
                        {signInRequired && !shouldShowData && <div css="padding: 20rem 0; text-align: center; width: 100%;">
                            {state.signInState === SignInState.Checking && <div>
                                Checking permissions...
                            </div>}
                            {state.signInState !== SignInState.Success && state.signInState !== SignInState.Checking && <div>
                                <h1>Please <a href="#" onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                                    event.preventDefault(); // prevent the default behavior of the hyperlink
                                    App.requestSignOutAndIn();
                                }}>Sign in</a> to view this website.</h1>
                            </div>}
                        </div>}
                        {state.signInState === SignInState.ChooseAccount && <ChooseAccount />}
                        {shouldShowData && <>
                            <NavBar />
                            <div css="width: 100%">
                                <Breadcrumb />
                                {state.releaseActionInfo.action !== ReleaseAction.Allow && <OutdatedBuildBanner
                                    releaseActionInfo={state.releaseActionInfo}
                                />}
                                <Switch>
                                    {/* Old DevCom1 -> DevCom2 redirect support to allow customers to use URLs
                                        from older emails and bookmarks
                                        problem:
                                        /problem/12345/some-post.html
                                        /content/problem/12345/some-post.html
                                            -> /t/some-post/12345

                                        idea:
                                        /idea/1277403/hide-itemgroup-items-in-solution-explorer.html
                                        /content/idea/1277403/hide-itemgroup-items-in-solution-explorer.html
                                            -> /t/hide-itemgroup-items-in-solution-explorer/1277403

                                        user:
                                        /users/374/1798b88c-c4a0-4aa7-85f1-e97eee80d9c3.html
                                            -> /user/1798b88c-c4a0-4aa7-85f1-e97eee80d9c3/profile
                                        (for unsubscribe link in email)
                                        /users/5094/52948721-04d8-46e7-a1e5-984497e7b7f8/preferences.html#notificationsPanel
                                            -> /user/52948721-04d8-46e7-a1e5-984497e7b7f8/profile

                                        home page:
                                        /spaces/8/index.html
                                            -> /search?space=8

                                        comments:
                                        /comments/1371473/view.html
                                            will be rewritten in PostDetailsRewriteView by calculating :ticketId to:
                                                -> /t/post-comment/:ticketId#T-ND1371473
                                    */}

                                    {/* 2022-10-12: nikolami: new URLs support added
                                        /:spaceSlug/... e.g. for "/VisualStudio" slug: 

                                        for new posts:
                                        /VisualStudio/report
                                        /VisualStudio/suggest
                                        /suggest

                                        details view:
                                        /VisualStudio/comments/12345/my_root_post_title <-- note: these URLs are supported in addition to /t/..., but not yet generated or rewritten to

                                        search view:
                                        /VisualStudio
                                    */}
                                    <Redirect from='/problem/:id/:slug.html' to={`${AppPaths.t}/:slug/:id`} />
                                    <Redirect from='/content/problem/:id/:slug.html' to={`${AppPaths.t}/:slug/:id`} />
                                    <Redirect from='/idea/:id/:slug.html' to={`${AppPaths.t}/:slug/:id`} />
                                    <Redirect from='/content/idea/:id/:slug.html' to={`${AppPaths.t}/:slug/:id`} />
                                    <Redirect from='/users/:ahId/:vsId.html' to={`/user/:vsId`} />
                                    <Redirect from='/users/:ahId/:vsId/:anyText.html' to={`/user/:vsId`} />
                                    <Redirect from='/spaces/:spaceId/:anyText.html' to={`/home`} />

                                    <Route path={AppPaths.home}>
                                        <HomeView />
                                    </Route>
                                    <Route path={AppPaths.fixes}>
                                        <RecentFixesView />
                                    </Route>
                                    <Route path={AppPaths.myfeedback}>
                                        <MyFeedbackView />
                                    </Route>
                                    <Route path={AppPaths.myactivity}>
                                        <MyActivityView />
                                    </Route>
                                    <Route path={[`/:${spaceSlug}${AppPaths.report}`, `/:${spaceSlug}${AppPaths.suggest}`]}>
                                        {isInReadOnlyMode && <ReadOnlyMode />}
                                        {!isInReadOnlyMode && <SubmitPostView />}
                                    </Route>
                                    <Route path={[`${AppPaths.t}/:${title}?/:${postId}`, `/:${spaceSlug}/${comments}/:${postId}/:${title}?`]}>
                                        <PostDetailsView />
                                    </Route>
                                    <Route path={`/comments/:commentId/:anyText.html`}>
                                        <PostDetailsRewriteView />
                                    </Route>
                                    <Route path={[`/user/:userId`, `/user/:userId/${AppPaths.profile}`]}>
                                        <ProfileView />
                                    </Route>
                                    <Route path={[AppPaths.search, `/:${spaceSlug}`]}>
                                        <SearchView />
                                    </Route>
                                    <Route path="*">
                                        <NoMatch />
                                    </Route>
                                </Switch>
                            </div>
                        </>}
                    </Content>
                </div>
                <GlobalNotificationDialog />
            </HashLocatorContext.Provider>
        </Wrapper >
    );
}

const ReadOnlyMode = (): JSX.Element => {
    return <ReadOnlyModeWrapper>
        <div><b>This section is not available because the website is currently in read-only mode.</b></div>
        <div>Reporting and editing features will be available again when the maintenance is complete.</div>
    </ReadOnlyModeWrapper>
}

const ReadOnlyModeWrapper = styled.div`
    padding-top: 5rem;
    font-size: 2rem;
    line-height: 4rem;

    * {
        text-align: center;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;

    ${forMinWidth.lg`
        flex-direction: row;
    `}
`;
